/**
 * PartyBankDetails
 *
 * Bank account list and add-new-bank-account form for vendor/supplier parties.
 * The in-progress form is stored in formData.pendingBankAccount so that
 * usePartyDialog.handleSave can flush it automatically.
 */

import { useCallback } from 'react';

import { EMPTY_PENDING_BANK } from './party-dialog-helpers';
import { FormField, Input, Button, useToast } from '../../ui';

import type { PartyFormData, PendingBankAccount } from './party-dialog-helpers';
import type { EntityBankAccount } from '../../../services/entity-service';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PartyBankDetailsProps {
  formData: PartyFormData;
  setFormData: React.Dispatch<React.SetStateAction<PartyFormData>>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function buildBankAccountFromPending(pending: PendingBankAccount): EntityBankAccount {
  return {
    accountName: pending.accountName.trim(),
    bankName: pending.bankName.trim() || undefined,
    accountNumber: pending.accountNumber.trim() || undefined,
    ifscCode: pending.ifscCode.trim() ? pending.ifscCode.trim().toUpperCase() : undefined,
    branch: pending.branch.trim() || undefined,
    swiftCode: pending.swiftCode.trim() ? pending.swiftCode.trim().toUpperCase() : undefined,
    iban: pending.iban.trim() ? pending.iban.trim().toUpperCase() : undefined,
    currency: (pending.currency.trim() || 'INR').toUpperCase(),
    accountType: 'current',
  };
}

interface PartyBankHandlers {
  setPending: (updater: Partial<PendingBankAccount>) => void;
  handleRemove: (idx: number) => void;
  handleAdd: () => void;
}

function usePartyBankHandlers(
  formData: PartyFormData,
  setFormData: React.Dispatch<React.SetStateAction<PartyFormData>>,
): PartyBankHandlers {
  const { addToast } = useToast();
  const pending: PendingBankAccount = formData.pendingBankAccount ?? EMPTY_PENDING_BANK;

  const setPending = useCallback((updater: Partial<PendingBankAccount>): void => {
    setFormData(prev => ({
      ...prev,
      pendingBankAccount: { ...(prev.pendingBankAccount ?? EMPTY_PENDING_BANK), ...updater },
    }));
  }, [setFormData]);

  const handleRemove = useCallback((idx: number): void => {
    setFormData(prev => {
      const updated = [...prev.bankAccounts];
      updated.splice(idx, 1);
      return { ...prev, bankAccounts: updated };
    });
  }, [setFormData]);

  const handleAdd = useCallback((): void => {
    if (!pending.accountName.trim()) {
      addToast({ variant: 'error', message: 'Account name is required' });
      return;
    }
    const newBa = buildBankAccountFromPending(pending);
    setFormData(prev => ({
      ...prev,
      bankAccounts: [...prev.bankAccounts, newBa],
      pendingBankAccount: EMPTY_PENDING_BANK,
    }));
  }, [pending, setFormData, addToast]);

  return { setPending, handleRemove, handleAdd };
}

export function PartyBankDetails({ formData, setFormData }: PartyBankDetailsProps): JSX.Element {
  const pending: PendingBankAccount = formData.pendingBankAccount ?? EMPTY_PENDING_BANK;
  const { setPending, handleRemove, handleAdd } = usePartyBankHandlers(formData, setFormData);

  return (
    <div>
      <h3 className="text-base font-semibold text-foreground el-section-heading el-mb-sm border-b border-border">
        Bank Details
      </h3>
      {formData.bankAccounts.length > 0 && (
        <div className="el-space-y-sm el-mb-sm">
          {formData.bankAccounts.map((ba, idx) => (
            <BankAccountRow key={ba.id ?? idx} account={ba} onRemove={() => handleRemove(idx)} />
          ))}
        </div>
      )}
      <AddBankAccountForm pending={pending} onAdd={handleAdd} onSet={setPending} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// BankAccountRow
// ---------------------------------------------------------------------------

interface BankAccountRowProps {
  account: EntityBankAccount;
  onRemove: () => void;
}

function BankAccountRow({ account, onRemove }: BankAccountRowProps): JSX.Element {
  return (
    <div className="el-bleed border-y border-border el-p-inner">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium">{account.accountName}</span>
        <Button size="sm" type="button" variant="ghost" onClick={onRemove}>
          Remove
        </Button>
      </div>
      <div className="text-xs text-foreground-secondary space-y-0.5">
        {account.bankName && (
          <span>{account.bankName}{account.branch ? ` — ${account.branch}` : ''}</span>
        )}
        {account.accountNumber && <span className="ml-2">A/C: {account.accountNumber}</span>}
        {account.ifscCode && <span className="ml-2">IFSC: {account.ifscCode}</span>}
        {account.swiftCode && <span className="ml-2">SWIFT: {account.swiftCode}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddBankAccountForm
// ---------------------------------------------------------------------------

interface AddBankAccountFormProps {
  pending: PendingBankAccount;
  onSet: (updater: Partial<PendingBankAccount>) => void;
  onAdd: () => void;
}

interface PendingFieldRowProps {
  pending: PendingBankAccount
  onSet: (updater: Partial<PendingBankAccount>) => void
}

function BasicPendingFields({ pending, onSet }: PendingFieldRowProps): JSX.Element {
  return (
    <>
      <FormField compact label="Account Name">
        <Input
          placeholder="e.g., HDFC Current Account"
          value={pending.accountName}
          onChange={e => onSet({ accountName: e.target.value })}
        />
      </FormField>
      <FormField compact label="Bank Name">
        <Input
          placeholder="e.g., HDFC Bank"
          value={pending.bankName}
          onChange={e => onSet({ bankName: e.target.value })}
        />
      </FormField>
      <FormField compact label="Account Number">
        <Input
          placeholder="Account number"
          value={pending.accountNumber}
          onChange={e => onSet({ accountNumber: e.target.value })}
        />
      </FormField>
      <FormField compact label="IFSC Code">
        <Input
          maxLength={11}
          placeholder="e.g., HDFC0001234"
          value={pending.ifscCode}
          onChange={e => onSet({ ifscCode: e.target.value.toUpperCase() })}
        />
      </FormField>
    </>
  )
}

function ExtendedPendingFields({ pending, onSet }: PendingFieldRowProps): JSX.Element {
  return (
    <>
      <FormField compact label="Branch">
        <Input
          placeholder="e.g., Mumbai Main"
          value={pending.branch}
          onChange={e => onSet({ branch: e.target.value })}
        />
      </FormField>
      <FormField compact label="Currency">
        <Input
          placeholder="INR"
          value={pending.currency}
          onChange={e => onSet({ currency: e.target.value })}
        />
      </FormField>
      <FormField compact label="SWIFT Code">
        <Input
          maxLength={11}
          placeholder="e.g., HDFCINBB"
          value={pending.swiftCode}
          onChange={e => onSet({ swiftCode: e.target.value.toUpperCase() })}
        />
      </FormField>
      <FormField compact label="IBAN">
        <Input
          maxLength={34}
          placeholder="If applicable"
          value={pending.iban}
          onChange={e => onSet({ iban: e.target.value.toUpperCase() })}
        />
      </FormField>
    </>
  )
}

function AddBankAccountForm({ pending, onSet, onAdd }: AddBankAccountFormProps): JSX.Element {
  return (
    <div className="el-space-y-sm el-mt-sm">
      <div className="grid grid-cols-2 el-gap-field">
        <BasicPendingFields pending={pending} onSet={onSet} />
        <ExtendedPendingFields pending={pending} onSet={onSet} />
      </div>
      <div className="el-bleed flex justify-end el-gap-sm">
        <Button size="sm" type="button" variant="secondary" onClick={onAdd}>
          Add Bank Account
        </Button>
      </div>
    </div>
  );
}
